<template>
  <div class="snapshot-page main-body" id="home-body">
    <!-- 地图 -->
    <LeafletConstructor ref="leafletRef" @mapInit="mapInit" :isAddBaseLayer="false" :layerControl="false"
      :search="true" />
    <!-- 左侧面板 -->
    <SnapshotPanel v-if="map" :map="map" class="snapshot-panel" @detailChange="onDetailChange" />

    <!-- 绘制工具栏 -->
    <div class="draw-toolbar" v-if="map && isDetailMode">
      <el-button-group>
        <el-button size="small" title="矩形" @click="drawRect" text>
          <svg-icon iconName="square" />
        </el-button>
        <el-button size="small" title="多边形" @click="drawPolygon" text>
          <svg-icon iconName="polygon" />
        </el-button>
        <el-button size="small" title="圆" @click="drawCircle" text>
          <svg-icon iconName="circle" />
        </el-button>
      </el-button-group>
    </div>

  </div>
</template>

<script setup>
import { ref, onUnmounted, toRaw, computed } from 'vue';
import * as turf from '@turf/turf';
import LeafletConstructor from '@/components/LeafletConstructor';
import SvgIcon from '@/components/SvgIcon';
import SnapshotPanel from './components/SnapshotPanel';
import { queryFixedBaseMap } from '@/api/UserWorkspaceDataApi';

const map = ref();
const leafletRef = ref();
const isDetailMode = ref(false);
const onDetailChange = (val) => { isDetailMode.value = val; };

// ===== 底图（L.control.layers） =====
let layerControl = null;
let currentBaseLayer = null;

const loadBaseMaps = () => {
  queryFixedBaseMap({}).then(res => {
    if (res.data.code == 1004) {
      const list = res.data.array || [];
      if (list.length === 0) return;
      const rawMap = toRaw(map.value);
      const baseLayers = {};
      list.forEach((item, index) => {
        const layer = L.tileLayer(item.url);
        baseLayers[item.name] = layer;
        if (index === 0) {
          layer.addTo(rawMap);
          currentBaseLayer = layer;
        }
      });
      if (Object.keys(baseLayers).length > 0) {
        layerControl = L.control.layers(baseLayers, null, {
          position: 'topright',
        }).addTo(rawMap);
      }
    }
  }).catch(err => { console.log(err); });
};

// ===== 绘制工具 =====
let drawTool = null;
const drawToolClear = () => {
  if (drawTool) {
    try { drawTool.disable(); } catch (e) { }
    drawTool = null;
  }
};

const drawRect = () => {
  drawToolClear();
  drawTool = new L.Draw.Rectangle(toRaw(map.value), {});
  drawTool.enable();
};
const drawPolygon = () => {
  drawToolClear();
  drawTool = new L.Draw.Polygon(toRaw(map.value), {});
  drawTool.enable();
};
const drawCircle = () => {
  drawToolClear();
  drawTool = new L.Draw.Circle(toRaw(map.value), {});
  drawTool.enable();
};

const mapDrawEventInit = (rawMap) => {
  rawMap.on(L.Draw.Event.CREATED, event => {
    let layer = event.layer;
    if (event.layerType === 'circle') {
      const latlng = layer.getLatLng();
      const geojson = turf.circle(
        [latlng.lng, latlng.lat],
        layer.getRadius() / 1000,
        { steps: 64, units: 'kilometers' }
      );
      L.geoJSON(geojson, {
        onEachFeature: (f, l) => { layer = l; }
      });
    }
    rawMap.fire(L.Draw.Event.DRAWSTOP, {
      layer, layerType: event.layerType === 'circle' ? 'polygon' : (event.layerType || 'polygon'),
    });
  });
};

// ===== 地图初始化 =====
const mapInit = (m) => {
  map.value = m;
  mapDrawEventInit(m);
  loadBaseMaps();
};

onUnmounted(() => {
  if (!map.value) return;
  const rawMap = toRaw(map.value);
  drawToolClear();
  rawMap.off && rawMap.off(L.Draw.Event.CREATED);
  if (layerControl) {
    rawMap.removeControl(layerControl);
    layerControl = null;
  }
});
</script>

<style lang='less' scoped>
.snapshot-page {
  height: 100%;
  width: 100%;
  position: relative;
  overflow: hidden;

  .snapshot-panel {
    --maringSize: 10px;
    position: absolute;
    left: var(--maringSize);
    top: var(--maringSize);
    width: 320px;
    max-height: calc(100% - calc(var(--maringSize) * 2));
    background-color: var(--domBackgroundColor);
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    border: 1px solid var(--borderColor);
    overflow: hidden;
  }

  .draw-toolbar {
    position: absolute;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    background-color: var(--domBackgroundColor);
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    padding: 4px 8px;

    .svg-icon {
      width: 18px;
      height: 18px;
    }
  }
}
</style>
